import { MenuItem } from '@material-ui/core';
import Autocomplete from '@material-ui/lab/Autocomplete';
import { UrlQueryKey } from 'app/core/api/api-core/api-constants';
import { useIdentity } from 'app/core/api/company/api-company';
import {
  useAssignedProduct,
  useAssignProduct,
  useSetAssignedProductReceiptImg,
  useUpdateAssignedProduct,
} from 'app/core/api/product-assignment/api-product-assignment';
import {
  currencyCodes,
  mapCurrencyCodeToSymbol,
} from 'app/core/api/product-assignment/api-product-assignment.types';
import { silenceError } from 'app/core/errors/error-utils';
import {
  MissingItemError,
  NotFoundError,
} from 'app/core/errors/error-variants';
import Button from 'app/shared/components/button/Button';
import FormDateInput from 'app/shared/components/form-input/FormDateInput';
import FormSelectInput from 'app/shared/components/form-input/FormSelectInput';
import FormTextInput from 'app/shared/components/form-input/FormTextInput';
import ImageInput from 'app/shared/components/form-input/ImageInput';
import {
  GENERAL_MAX_LENGTH,
  maxLengthRule,
} from 'app/shared/components/form-input/utilities/form-tools';
import useInputForm from 'app/shared/components/form-input/utilities/use-input-form';
import Icon from 'app/shared/components/icon/Icon';
import IconLib from 'app/shared/components/icon/icon-lib';
import CollapseSection from 'app/shared/components/list/CollapseSection';
import {
  ErrorText,
  InputTitle,
  PadExpand,
} from 'app/shared/components/misc';
import Body from 'app/shared/components/typography/body/Body';
import { useShowInfo } from 'app/shared/context-hooks/use-show-info.hook';
import { useSetPageTitle } from 'app/shared/context-hooks/use-titlebar.hook';
import Form from 'app/shared/frames/Form';
import MultiPartInputBox from 'app/shared/frames/MultiPartInputBox';
import SharedRoutePath from 'app/shared/SharedRoutePath';
import { useQueryParams } from 'app/tools/hooks/misc-hooks';
import React, { useEffect, useMemo, useState } from 'react';
import { Redirect, useHistory } from 'react-router-dom';
import NotFoundRedirect from '../components/NotFoundRedirect';
import {
  AssignedProductDetailsFormValues,
  countriesList,
  FullCountry,
  mapDefaults,
  useHandleSubmitAssignedProductDetails,
} from './add-assigned-product-details-utils';

const rules = maxLengthRule(GENERAL_MAX_LENGTH);

export default function AddAssignedProductDetails(): JSX.Element {
  const params = useQueryParams();
  const productVariantId = params.get(UrlQueryKey.PRODUCT_VARIANT_ID);
  const productVariantName = params.get(
    UrlQueryKey.PRODUCT_VARIANT_NAME
  );
  const productAssignmentId = Number(
    params.get(UrlQueryKey.PRODUCT_ASSIGNMENT_ID)
  );
  const history = useHistory();
  const { showSnack } = useShowInfo();

  useSetPageTitle(productVariantName);

  const [countrySelect, setCountrySelect] =
    useState<FullCountry | null>(null);
  const [purchasePriceCurrency, setPurchasePriceCurrency] =
    useState<string>('');
  const [isFromSale, setIsFromSale] = useState('false');

  const isBalsamHill = productVariantName?.includes('Balsam Hill');

  const {
    resource: assignedProduct,
    error,
    loading,
  } = useAssignedProduct(productAssignmentId, {
    // only automatically fetch item if not adding details for the first time
    dontAutoFetch: !productAssignmentId,
    fresh: !!productAssignmentId,
  });

  const { resource: identity } = useIdentity();
  const defaultValues = useMemo(
    () => mapDefaults(assignedProduct, identity?.locale),
    [identity?.locale, assignedProduct]
  );

  // NOTE: this is null if adding new product (aka missing assignedProductId),
  // otherwise assumed updating product: undefined if still loading,
  // true if all okay, and false if no product found matching assignedProductId
  const updateModeLoaded =
    (!!productAssignmentId || null) &&
    (!loading || undefined) &&
    !error;

  const { sending: sendingAssignment, error: productAssignError } =
    useAssignProduct();
  const { sending: sendingUpdate, error: productUpdateError } =
    useUpdateAssignedProduct();
  const { sending: sendingImage, error: imageUploadError } =
    useSetAssignedProductReceiptImg();

  const submitError =
    productAssignError || productUpdateError || imageUploadError;
  const submitPending =
    sendingAssignment || sendingUpdate || sendingImage;

  // eslint-disable-next-line @typescript-eslint/unbound-method
  const { handleSubmit, setError, inputProps, setValue } =
    useInputForm<AssignedProductDetailsFormValues>({
      apiError: submitError,
      defaultValues,
    });

  // Set initial value for country dropdown box
  useEffect(() => {
    const foundCountry = countriesList.find(
      country => country.code === defaultValues?.purchaseCountry
    );
    if (foundCountry) {
      setPurchasePriceCurrency(
        defaultValues?.purchasePriceCurrency ?? ''
      );
      setValue('purchaseCountry', foundCountry.name);
      setValue(
        'purchasePriceCurrency',
        defaultValues?.purchasePriceCurrency ?? ''
      );
      setCountrySelect(foundCountry);
    }
  }, [
    defaultValues?.purchaseCountry,
    defaultValues?.purchasePriceCurrency,
    setValue,
  ]);

  useEffect(() => {
    setValue('isFromSale', isFromSale);
  }, [isFromSale, setValue]);

  const handleIsFromSale = (
    event: React.ChangeEvent<{ value: unknown }>
  ) => {
    setValue('isFromSale', event.target.value as string);
    setIsFromSale(event.target.value as string);
  };

  const [imgUpload, setImgUpload] = useState<File | null>(null);

  const [submitted, setSubmitted] = useState(false);
  const handleSubmitAssignedProductDetails =
    useHandleSubmitAssignedProductDetails({
      updateModeLoaded,
      imgUpload,
    });

  const onSubmit = (formValues: AssignedProductDetailsFormValues) => {
    if (!countrySelect) throw new MissingItemError('countrySelect');
    // eslint-disable-next-line no-param-reassign
    formValues.purchasePriceCurrency = purchasePriceCurrency;
    handleSubmitAssignedProductDetails(
      formValues,
      countrySelect
    ).subscribe({
      next: ({ message, action }) => {
        showSnack({ message, action });
        setSubmitted(true);
        if (updateModeLoaded) history.goBack();
      },
      ...silenceError,
    });
  };

  if (!productVariantId || !productVariantName) {
    throw new NotFoundError('product ID or name');
  }
  if (updateModeLoaded === false) {
    return <NotFoundRedirect />;
  }
  if (submitted && updateModeLoaded === null) {
    return <Redirect to={SharedRoutePath.HOME} />;
  }

  return (
    <>
      <Form onSubmit={handleSubmit(onSubmit)} autoComplete="off">
        <InputTitle>When did you purchase your product?</InputTitle>
        <FormDateInput
          optional
          classicPlaceholder
          placeholder="Date purchased"
          controlName="purchaseDate"
          disabled={updateModeLoaded === undefined}
          {...{ setError }}
          {...inputProps}
        />

        <InputTitle>Enter the store name & location</InputTitle>
        <FormTextInput
          optional
          classicPlaceholder
          controlName="purchasePlace"
          placeholder="Shop or online company name"
          required={false}
          disabled={updateModeLoaded === undefined}
          {...{ rules }}
          {...inputProps}
        />

        <InputTitle>How much did you pay for the item?</InputTitle>
        <MultiPartInputBox variant="flush" gridCols="6em 1fr">
          <FormSelectInput
            optional
            classicPlaceholder
            controlName="purchasePriceCurrency"
            disabled={updateModeLoaded === undefined}
            value={purchasePriceCurrency}
            onChange={e =>
              setPurchasePriceCurrency(e.target.value as string)
            }
            {...{ rules }}
            {...inputProps}
          >
            {Object.keys(currencyCodes).map(code => (
              <MenuItem value={code} key={code}>
                {mapCurrencyCodeToSymbol.get(code)}
              </MenuItem>
            ))}
          </FormSelectInput>
          <FormTextInput
            optional
            classicPlaceholder
            controlName="purchasePriceAmount"
            placeholder="Value"
            disabled={updateModeLoaded === undefined}
            label=""
            type="number"
            inputProps={{
              step: 'any',
            }}
            rules={{
              ...rules,
              max: {
                value: 99999999,
                message: "I think it's unlikely you paid that much!",
              },
              min: {
                value: 0,
                message: 'Value must be positive',
              },
            }}
            {...inputProps}
          />
        </MultiPartInputBox>

        <InputTitle>Purchase country</InputTitle>
        <Autocomplete
          options={countriesList}
          getOptionLabel={o => o.name}
          value={countrySelect}
          autoSelect
          autoHighlight
          disabled={updateModeLoaded === undefined}
          onChange={(_e, v) => {
            if (v) {
              setCountrySelect(v);
              setValue('purchaseCountry', v.name);
            }
          }}
          renderInput={props => (
            <FormTextInput
              classicPlaceholder
              controlName="purchaseCountry"
              placeholder="Country of purchase"
              {...{ rules }}
              {...inputProps}
              {...props}
            />
          )}
        />

        {isBalsamHill ? (
          <>
            <InputTitle>Was this a clearance item?</InputTitle>
            <FormSelectInput
              optional
              classicPlaceholder
              controlName="isFromSale"
              disabled={updateModeLoaded === undefined}
              value={isFromSale}
              onChange={handleIsFromSale}
              {...inputProps}
            >
              <MenuItem value="true">Yes</MenuItem>
              <MenuItem value="false">No</MenuItem>
            </FormSelectInput>
          </>
        ) : (
          <FormTextInput
            style={{ display: 'none' }}
            classicPlaceholder
            controlName="isFromSale"
            placeholder="Were you purchase a clearance item?"
            disabled={updateModeLoaded === undefined}
            value={isFromSale}
            {...{ rules }}
            {...inputProps}
          />
        )}

        {/* Acts as a spacer. Has margin provided by Form child rules */}
        <div />

        <CollapseSection title="Additional details & warranty information">
          <InputTitle>Product serial number</InputTitle>
          <FormTextInput
            optional
            classicPlaceholder
            disabled={updateModeLoaded === undefined}
            controlName="serialNumber"
            placeholder="Serial number"
            {...{ rules }}
            {...inputProps}
          />

          <InputTitle>Custom warranty information</InputTitle>
          <Body small withNext>
            Fill in zero (0) if you know you have no warranty, or
            leave blank if you are not sure
          </Body>
          <MultiPartInputBox variant="flush">
            <FormTextInput
              classicPlaceholder
              controlName="expiryTerm"
              placeholder="Duration"
              disabled={updateModeLoaded === undefined}
              type="number"
              inputProps={{ step: 'any' }}
              optional
              rules={{
                ...rules,
                max: {
                  value: 999,
                  message: 'Duration is too large',
                },
                min: {
                  value: 0,
                  message: 'Duration must be positive',
                },
              }}
              {...inputProps}
            />
            <FormSelectInput
              classicPlaceholder
              controlName="durationUnits"
              disabled={updateModeLoaded === undefined}
              {...{ rules }}
              {...inputProps}
            >
              <MenuItem value="months">months</MenuItem>
              <MenuItem value="years">years</MenuItem>
            </FormSelectInput>
          </MultiPartInputBox>
        </CollapseSection>

        <InputTitle>Upload a receipt or invoice</InputTitle>
        <ImageInput
          acceptType="image/jpeg,image/jpg,image/png,application/pdf"
          buttonText={change => (
            <>
              {change ? 'Change' : 'Upload'}&nbsp;&nbsp;
              <Icon icon={IconLib.ARROW_UP_BOXED} color="white" />
            </>
          )}
          onFileSelected={setImgUpload}
          buttonProps={{
            slim: true,
            style: { minWidth: '60%' },
            disabled: updateModeLoaded === undefined,
          }}
          initialFileUrl={assignedProduct?.receiptImage}
        />

        <PadExpand />

        <ErrorText message={submitError} />
        <Button
          type="submit"
          pending={submitPending}
          disabled={updateModeLoaded === undefined}
        >
          {updateModeLoaded ? 'Update' : 'Save'} product
        </Button>
      </Form>
    </>
  );
}
